/**
 * Adaptive Intelligence Context
 * Tracks user behavior and adapts dashboard accordingly
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AdaptiveIntelligenceContext = createContext();

const DEFAULT_BEHAVIOR = {
  views: {},
  actions: {},
  dismissals: {},
  preferences: {},
};

export const AdaptiveIntelligenceProvider = ({ children }) => {
  const [behavior, setBehavior] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('adaptiveBehavior');
      return saved ? JSON.parse(saved) : DEFAULT_BEHAVIOR;
    }
    return DEFAULT_BEHAVIOR;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('adaptiveBehavior', JSON.stringify(behavior));
    }
  }, [behavior]);

  const trackView = useCallback((itemId, itemType) => {
    setBehavior((prev) => ({
      ...prev,
      views: {
        ...prev.views,
        [itemId]: (prev.views[itemId] || 0) + 1,
      },
    }));
  }, []);

  const trackAction = useCallback((actionType, itemId) => {
    setBehavior((prev) => ({
      ...prev,
      actions: {
        ...prev.actions,
        [`${actionType}-${itemId}`]: (prev.actions[`${actionType}-${itemId}`] || 0) + 1,
      },
    }));
  }, []);

  const trackDismissal = useCallback((itemId) => {
    setBehavior((prev) => ({
      ...prev,
      dismissals: {
        ...prev.dismissals,
        [itemId]: (prev.dismissals[itemId] || 0) + 1,
      },
    }));
  }, []);

  const getItemScore = useCallback((itemId) => {
    const views = behavior.views[itemId] || 0;
    const actions = Object.entries(behavior.actions)
      .filter(([key]) => key.endsWith(`-${itemId}`))
      .reduce((sum, [, count]) => sum + count, 0);
    const dismissals = behavior.dismissals[itemId] || 0;
    
    // Score = views + actions - dismissals
    return views + actions * 2 - dismissals * 3;
  }, [behavior]);

  const getInsight = useCallback((itemId, itemType) => {
    const score = getItemScore(itemId);
    const views = behavior.views[itemId] || 0;
    const actions = Object.entries(behavior.actions)
      .filter(([key]) => key.endsWith(`-${itemId}`))
      .reduce((sum, [, count]) => sum + count, 0);

    if (views > 5 && actions > 0) {
      return `You usually interact with ${itemType} items like this.`;
    }
    if (score < 0) {
      return `You've dismissed similar items before.`;
    }
    return null;
  }, [behavior, getItemScore]);

  const getExplanation = useCallback((itemId, itemType) => {
    const score = getItemScore(itemId);
    const views = behavior.views[itemId] || 0;
    
    if (score > 10) {
      return `Shown because you frequently view and interact with ${itemType} items.`;
    }
    if (views > 3) {
      return `Shown because you've viewed similar items ${views} times.`;
    }
    return `Shown based on your activity patterns.`;
  }, [behavior, getItemScore]);

  return (
    <AdaptiveIntelligenceContext.Provider value={{
      behavior,
      trackView,
      trackAction,
      trackDismissal,
      getItemScore,
      getInsight,
      getExplanation,
    }}>
      {children}
    </AdaptiveIntelligenceContext.Provider>
  );
};

export const useAdaptiveIntelligence = () => useContext(AdaptiveIntelligenceContext);

